"use client";
import { useEffect, useState } from "react";
import { Thermometer, Droplets, Wind, MapPin, RefreshCw, AlertTriangle } from "lucide-react";

// Proxied through /api/weather so same-origin fetch is never blocked by CSP.
const WEATHER_URL = "/api/weather";

type WeatherData = {
  temp: number;
  feelsLike: number;
  rh: number;
  dewPoint: number;
  windMph: number;
  weatherCode: number;
  fetchedAt: Date;
};

function weatherLabel(code: number): string {
  if (code === 0) return "Clear sky";
  if (code <= 3) return "Partly cloudy";
  if (code <= 49) return "Foggy";
  if (code <= 55) return "Drizzle";
  if (code <= 67) return "Rain";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Rain showers";
  if (code <= 86) return "Snow showers";
  if (code <= 99) return "Thunderstorm";
  return "Unknown";
}

// WMO drying advisory based on RH + temp
function dryingAdvisory(rh: number, tempF: number): { label: string; color: string } {
  if (rh <= 40 && tempF >= 70) return { label: "Excellent drying conditions", color: "#4ade80" };
  if (rh <= 55 && tempF >= 65) return { label: "Good drying conditions", color: "#86efac" };
  if (rh <= 70) return { label: "Moderate — monitor GPP carefully", color: "#D4A84B" };
  return { label: "High ambient RH — dehu load will be elevated", color: "#f87171" };
}

export function WeatherBanner() {
  const [data, setData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchWeather = () => {
    setLoading(true);
    setError(false);
    fetch(WEATHER_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        const c = json?.current;
        if (!c) throw new Error("missing current");
        setData({
          temp: Math.round(c.temperature_2m),
          feelsLike: Math.round(c.apparent_temperature),
          rh: Math.round(c.relative_humidity_2m),
          dewPoint: Math.round(c.dew_point_2m),
          windMph: Math.round(c.wind_speed_10m),
          weatherCode: c.weather_code,
          fetchedAt: new Date(),
        });
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWeather();
  }, []);

  const advisory = data ? dryingAdvisory(data.rh, data.temp) : null;

  return (
    <section style={{ padding: "0 24px 0", position: "relative", zIndex: 1 }}>
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto 0",
          background: "rgba(13,25,41,0.85)",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
          padding: "18px 28px",
          marginTop: "-20px",
        }}
      >
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="size-4 shrink-0" style={{ color: "#D4A84B" }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.55)" }}>
              Live Conditions · Tempe, AZ
            </span>
          </div>
          <button
            onClick={fetchWeather}
            title="Refresh weather"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "rgba(255,255,255,0.40)",
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
            }}
          >
            <RefreshCw className={`size-3 ${loading ? "animate-spin" : ""}`} />
            {data && !loading && (
              <span>
                {data.fetchedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </button>
        </div>

        {/* Data row */}
        {loading && !data && (
          <div className="mt-3 flex items-center gap-2" style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>
            <RefreshCw className="size-3 animate-spin" /> Fetching current conditions…
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-center gap-2" style={{ color: "#f87171", fontSize: 13 }}>
            <AlertTriangle className="size-3.5" /> Unable to load weather — check network connection.
          </div>
        )}

        {data && (
          <>
            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3">
              {/* Temp */}
              <div className="flex items-center gap-2">
                <Thermometer className="size-4 shrink-0" style={{ color: "#D4A84B" }} />
                <div>
                  <p className="text-2xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {data.temp}°F
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Feels {data.feelsLike}°F · {weatherLabel(data.weatherCode)}
                  </p>
                </div>
              </div>

              {/* RH */}
              <div className="flex items-center gap-2">
                <Droplets className="size-4 shrink-0" style={{ color: "#60a5fa" }} />
                <div>
                  <p className="text-2xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {data.rh}%
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Relative Humidity
                  </p>
                </div>
              </div>

              {/* Dew Point */}
              <div className="flex items-center gap-2">
                <Droplets className="size-4 shrink-0" style={{ color: "#93c5fd" }} />
                <div>
                  <p className="text-2xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {data.dewPoint}°F
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Dew Point
                  </p>
                </div>
              </div>

              {/* Wind */}
              <div className="flex items-center gap-2">
                <Wind className="size-4 shrink-0" style={{ color: "#a3e635" }} />
                <div>
                  <p className="text-2xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {data.windMph} mph
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Wind Speed
                  </p>
                </div>
              </div>
            </div>

            {/* Drying advisory */}
            {advisory && (
              <div
                className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: advisory.color + "18",
                  border: `1px solid ${advisory.color}40`,
                  color: advisory.color,
                }}
              >
                <span>⬤</span> {advisory.label}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
